/* eslint-disable complexity */
import { headerMiniCartItemSX } from '@/components/content/Header/styles/miniCart/item';
import { useCart } from '@/data/Content/Cart';
import { useLocalization } from '@/data/Localization';
import { Box, ButtonBase, ClickAwayListener, Divider, Stack, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { FC, useMemo, useRef } from 'react';
import { HeaderMiniCartDropMenuItem } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItem';
import ClearIcon from '@mui/icons-material/Clear';
import { HeaderMiniCartDropMenuItemSkeleton } from '@/components/content/Header/parts/HeaderMiniCartDropMenuItemSkeleton';
import { useOrderItemListRowsAvailability } from '@/data/Content/OrderItemInventory';
import { useNotifications } from '@/data/Content/Notifications';

import { Img } from '@/components/blocks/MaterialImage';
import { useBasicCheckOut } from '@/data/Content/BasicCheckOut';
import { OrderSummaryBreakdown } from '@/components/content/Cart/parts/OrderSummaryBreakdown';
import { useUser } from '@/data/User';
import { AuthentificationFlow } from '@/components/blocks/AuthentificationFlow';
// eslint-disable-next-line no-restricted-imports
import { useIsOpen } from 'integration/hooks/useIsOpen';
import { ProductType } from '@/data/types/Product';
import { useMobileHeaderQuery } from '@/components/content/Header';
import {
	actionsStackSX,
	cartButtonSX,
	cartFooterSX,
	checkoutButtonSX,
	closeIconSX,
	containerSX,
	emptyCloseIconSX,
	emptyContainerSX,
	emptyMessageSX,
	headerIconImageSX,
	headerRowSX,
	headerTitleSX,
	itemsStackSX,
	productCountNumberSX,
	productCountSX,
	recommendationsDividerSX,
	recommendationsInnerSX,
	recommendationsTitleSX,
	recommendationsWrapperSX,
	scrollAreaSX,
	totalRowDividerSX,
	totalRowSX,
} from '@/components/content/Header/parts/MiniCartDropMenu/styles';
import { CLICK_PRODUCT_VIEW_SOURCE } from '@/data/constants/clickProductView';
import { CatalogEntryIARecommendationFree } from '@/components/content/CatalogEntryIARecommendation/free';
import { getLastAddedChildPartNumber } from '@/utils/getLastAddedChildPartNumber';
import { partNumbersIARecommendationExtractorByUnknown } from '@/utils/partNumbersIARecommendationExtractorByUnknown';
import { useQuickCartEvents } from '@/data/Content/QuickCartEvents';

const iaEmptyExtractor = (products: ProductType[]) => !products || products.length === 0;

export const HeaderMiniCartDropMenu: FC<{
	fromLocal: boolean;
	onClose: () => void;
	onOpenFreeShippingActivatedPopup: (resumeCheckout: () => void) => Promise<boolean>;
	// eslint-disable-next-line complexity
}> = ({ fromLocal, onClose, onOpenFreeShippingActivatedPopup }) => {
	const CartLabels = useLocalization('MiniCart');
	const RouteLabels = useLocalization('Routes');
	const { orderItems, data: order, checkout, canContinue, getCount, router } = useCart();
	const isMobile = useMobileHeaderQuery();
	const MAX_ROWS = isMobile ? 2 : 3;
	const { firstNRows } = useMemo(() => {
		const firstNRows = orderItems?.slice(0, MAX_ROWS);
		const firstNRowsCount = getCount(firstNRows);
		return { firstNRows, firstNRowsCount };
	}, [MAX_ROWS, getCount, orderItems]);

	const lastAddedPartNumber = useMemo(() => getLastAddedChildPartNumber(orderItems), [orderItems]);
	const { trackQuickCartClick } = useQuickCartEvents(lastAddedPartNumber);
	const hasEnoughHeight = useMediaQuery('(min-height:600px)', {
		noSsr: true,
		defaultMatches: true,
	});

	const { totalCoupons, totalDiscount, totalPriceWithoutOffer, orderItemListRowsSorted } =
		useBasicCheckOut({
			adjustment: order?.adjustment || [],
			orderItem: order?.orderItem || [],
			orderId: order?.orderId || '',
		});

	const { grandTotal = '0', totalShippingCharge = '0' } = order || {};

	const { showSuccessMessage } = useNotifications();
	const {
		orderItemListRowsWithAvailability,
		orderItemListRowsNotAvailable,
		loadingOrderItemListRowsAvailability,
	} = useOrderItemListRowsAvailability(orderItemListRowsSorted);

	const loading =
		orderItemListRowsWithAvailability.length === 0 || loadingOrderItemListRowsAvailability;

	const disabled = loading ? true : canContinue ? !canContinue() : false;

	const { user: { isLoggedIn = false } = {} } = useUser();

	const openAuthFlow = useIsOpen();
	const justClosedAuth = useRef(false);

	const handleAuthClose = () => {
		justClosedAuth.current = true;
		openAuthFlow.off();
		setTimeout(() => {
			justClosedAuth.current = false;
		}, 200);
	};

	const continueCheckout = () => {
		if (!isLoggedIn) {
			openAuthFlow.on();
			return;
		}

		checkout();
	};

	const handleOnClickCheckout = async () => {
		trackQuickCartClick(orderItemListRowsNotAvailable.length > 0 ? 'cart' : 'checkout');
		if (orderItemListRowsNotAvailable.length > 0) {
			router.push(RouteLabels.Cart.route.t()).finally(() => {
				showSuccessMessage(
					'Revisa tus productos del carrito y elimina el producto caducado para continuar tu compra.',
					'TIENES UN PRODUCTO CADUCADO'
				);
			});

			return;
		}

		if (await onOpenFreeShippingActivatedPopup(continueCheckout)) {
			return;
		}

		continueCheckout();
	};

	const handleOnClickCart = () => {
		trackQuickCartClick('cart');
		const path = globalThis.location.pathname;
		if (path.includes(RouteLabels.Cart.route.t())) {
			onClose();
			return;
		}
		router.push(RouteLabels.Cart.route.t());
	};

	const handleOnClickAway = () => {
		if (openAuthFlow.open || justClosedAuth.current) return;
		onClose();
	};

	if ((firstNRows?.length || 0) === 0) {
		return (
			<ClickAwayListener onClickAway={handleOnClickAway}>
				<Stack sx={emptyContainerSX}>
					<Stack spacing={1} alignItems="center" sx={headerMiniCartItemSX}>
						<Typography variant="body1" align="center" sx={emptyMessageSX}>
							{CartLabels.Empty.t()}
						</Typography>
						<ClearIcon sx={emptyCloseIconSX} onClick={onClose} />
					</Stack>
				</Stack>
			</ClickAwayListener>
		);
	}

	return (
		<>
			<ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleOnClickAway}>
				<Box sx={containerSX}>
					<Stack direction="row" sx={headerRowSX}>
						<Typography sx={headerTitleSX}>
							{!fromLocal ? (
								<Img src="/images/check-circle.png" alt="check-circle" sx={headerIconImageSX} />
							) : null}
							{fromLocal
								? `Carrito de compras (${orderItemListRowsWithAvailability.length})`
								: 'Agregaste al carrito'}
						</Typography>
						<ClearIcon htmlColor="#929292" sx={closeIconSX} onClick={onClose} />
					</Stack>
					<Box sx={scrollAreaSX}>
						<Stack spacing={1} sx={itemsStackSX}>
							{!loading ? (
								<React.Fragment>
									{orderItemListRowsWithAvailability.map((product) => (
										<HeaderMiniCartDropMenuItem
											key={product.orderItemListRow.orderItem.orderItemId}
											thumbnail={product.details.thumbnail}
											name={product.details.name}
											attributes={product.details.attributes}
											offerPrice={product.details.prices.offer || 0}
											displayPrice={product.details.prices.list || 0}
											totalAdjustment={product.orderItemListRow.totalAdjustment.totalAdjustment}
											orderItemPrice={product.orderItemListRow.price.orderItemPrice}
											exhausted={product.availability?.status === false}
											href={product.details.seo?.href}
											orderId={order?.orderId || ''}
											orderItemId={product.orderItemListRow.orderItem.orderItemId}
										/>
									))}
									<Box sx={totalRowSX}>
										<Divider sx={totalRowDividerSX} />
										<OrderSummaryBreakdown
											showTitle={false}
											total={grandTotal}
											shipping={totalShippingCharge}
											discount={totalDiscount}
											subtotal={totalPriceWithoutOffer}
											coupons={totalCoupons}
											isMiniCart
											adjustment={order?.adjustment}
										/>
									</Box>
								</React.Fragment>
							) : (
								<HeaderMiniCartDropMenuItemSkeleton />
							)}
						</Stack>
					</Box>
					{hasEnoughHeight && lastAddedPartNumber ? (
						<CatalogEntryIARecommendationFree
							params={{ partNumber: lastAddedPartNumber }}
							clickSource={CLICK_PRODUCT_VIEW_SOURCE.QUICK_CART_RECOMMENDED}
							partNumbersExtractor={(data) => partNumbersIARecommendationExtractorByUnknown('outfit', data)}
							emptyExtractor={iaEmptyExtractor}
							render={(component) => (
								<>
									<Divider sx={recommendationsDividerSX} />
									<Box sx={recommendationsWrapperSX}>
										<Box sx={recommendationsInnerSX}>
											<Typography sx={recommendationsTitleSX}>{'Te puede interesar'}</Typography>
											{component}
										</Box>
									</Box>
								</>
							)}
						/>
					) : null}
					<Box sx={cartFooterSX}>
						<Typography component="span" sx={productCountSX}>
							{'Hay '}
							<Typography component="span" sx={productCountNumberSX}>
								{orderItemListRowsWithAvailability.length}
							</Typography>
							{' productos en tu carrito'}
						</Typography>
						<Stack
							direction="row"
							spacing="14px"
							justifyContent="center"
							alignItems="center"
							sx={actionsStackSX}
						>
							<ButtonBase
								id="mini-cart-view-full-cart-button"
								data-testid="mini-cart-view-full-cart-button"
								sx={cartButtonSX}
								onClick={handleOnClickCart}
							>
								{'Ver carrito'}
							</ButtonBase>
							<ButtonBase sx={checkoutButtonSX} disabled={disabled} onClick={handleOnClickCheckout}>
								{'Ir a pagar'}
							</ButtonBase>
						</Stack>
					</Box>
				</Box>
			</ClickAwayListener>
			<AuthentificationFlow
				open={openAuthFlow.open}
				type="login"
				origin="quick_cart"
				onClose={handleAuthClose}
			/>
		</>
	);
};
